import Chart from 'chart.js/auto';
import c from 'classnames';
import { useEffect, useRef } from 'react';
import { Typography } from '../Typography';
import styles from './GaugeChart.module.css';
import { Color, GaugeRenderOptions, getConfig } from './getConfig';

type Props = {
  label: string;
  labelSize?: string;
  color: Color;
  width?: number;
  height?: number;
  overrideColors?: Partial<Record<Color, string>>;
  overrideTextColors?: Partial<Record<Color, string>>;
  extraOptions?: GaugeRenderOptions;
};

const COLORS: Record<Color, string> = {
  red: '#DA5D5D',
  orange: '#EDA732',
  grey: '#3F4139',
};

const VALUES: Record<Color, number> = {
  red: 33,
  orange: 66,
  grey: 100,
};

export function GaugeChart({
  label,
  labelSize,
  color,
  width,
  height,
  overrideColors,
  overrideTextColors,
  extraOptions,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const value = VALUES[color];

  const colorLookup = overrideColors
    ? { ...COLORS, ...overrideColors }
    : COLORS;
  const parsedColor = colorLookup[color];

  const textColorLookup = overrideColors
    ? { ...COLORS, ...overrideTextColors }
    : COLORS;
  const parsedTextColor = textColorLookup[color];

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const chart = new Chart(ctx, getConfig(value, parsedColor, extraOptions));

    return () => chart.destroy();
  }, [extraOptions, parsedColor, value]);

  return (
    <div className={c(styles.container)}>
      <canvas
        id='gaugeChart'
        ref={canvasRef}
        style={{ maxWidth: width ?? 250, maxHeight: height ?? 120 }}
      />
      <Typography
        variant='label'
        style={{ color: parsedTextColor, fontSize: labelSize }}
        className={c(styles.label)}
      >
        {label}
      </Typography>
    </div>
  );
}
